import React, { useState, useEffect } from 'react';
import RecorderCard from './RecorderCard';

export default function Dashboard({ backendUrl, user, language }) {
  const [activeTest, setActiveTest] = useState('vertical_jump');
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);

  const tests = [
    { 
      id: 'vertical_jump', 
      name: language === 'hi' ? 'वर्टिकल जंप' : 'Vertical Jump', 
      emoji: '🏀', 
      desc: language === 'hi' ? 'जंप की ऊंचाई मापें' : 'Measure jump height',
      color: 'from-orange-400 to-red-500'
    },
    { 
      id: 'situps', 
      name: language === 'hi' ? 'सिट-अप्स' : 'Sit-ups', 
      emoji: '💪', 
      desc: language === 'hi' ? 'सिट-अप्स गिनें' : 'Count sit-ups',
      color: 'from-blue-400 to-indigo-500'
    },
    { 
      id: 'shuttle_run', 
      name: language === 'hi' ? 'शटल रन' : 'Shuttle Run', 
      emoji: '🏃', 
      desc: language === 'hi' ? '20m शटल रन का समय' : 'Time 20m shuttle',
      color: 'from-green-400 to-emerald-500'
    }
  ];

  useEffect(() => {
    fetchSubmissions();
    fetchStats();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backendUrl}/api/submissions?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSubmissions(data.results || []);
      }
    } catch (error) {
      console.error('Fetch submissions error:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backendUrl}/api/submissions/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
    }
  };

  const t = (key) => {
    const translations = {
      en: {
        welcome: 'Welcome back',
        selectTest: 'Select a Test',
        yourActivity: 'Your Recent Activity',
        quickTips: 'Quick Tips',
        tip1: 'Use a plain background for better pose detection',
        tip2: 'Keep the phone stable at chest height',
        tip3: 'Follow on-screen countdown before starting',
        tip4: 'Ensure good lighting for accurate results',
        noSubmissions: 'No submissions yet. Start your first test!',
        score: 'Score',
        status: 'Status',
        viewAll: 'View All'
      },
      hi: {
        welcome: 'वापसी पर स्वागत है',
        selectTest: 'एक टेस्ट चुनें',
        yourActivity: 'आपकी हाल की गतिविधि',
        quickTips: 'त्वरित सुझाव',
        tip1: 'बेहतर पोज़ डिटेक्शन के लिए सादे बैकग्राउंड का उपयोग करें',
        tip2: 'फ़ोन को छाती की ऊंचाई पर स्थिर रखें',
        tip3: 'शुरू करने से पहले ऑन-स्क्रीन काउंटडाउन का पालन करें',
        tip4: 'सटीक परिणामों के लिए अच्छी रोशनी सुनिश्चित करें',
        noSubmissions: 'अभी तक कोई सबमिशन नहीं। अपना पहला टेस्ट शुरू करें!',
        score: 'स्कोर',
        status: 'स्थिति',
        viewAll: 'सभी देखें'
      }
    };
    return translations[language][key] || key;
  };

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {t('welcome')}, {user.name}! 👋
            </h1>
            <p className="text-white/90 text-lg">
              {language === 'hi' 
                ? 'आज अपनी क्षमता दिखाने के लिए तैयार हैं?' 
                : 'Ready to showcase your abilities today?'}
            </p>
          </div>
          <div className="hidden md:block">
            <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-6xl">
              🏅
            </div>
          </div>
        </div>
      </div>

      {/* Test Selection Cards */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {t('selectTest')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tests.map(test => (
            <button
              key={test.id}
              onClick={() => setActiveTest(test.id)}
              className={`relative overflow-hidden rounded-2xl p-6 text-left transition-all duration-300 ${
                activeTest === test.id
                  ? 'scale-105 shadow-2xl'
                  : 'hover:scale-102 shadow-lg'
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${test.color} ${
                activeTest === test.id ? 'opacity-100' : 'opacity-70'
              }`}></div>
              
              <div className="relative z-10 text-white">
                <div className="text-5xl mb-3">{test.emoji}</div>
                <h3 className="text-xl font-bold mb-1">{test.name}</h3>
                <p className="text-sm text-white/80 mb-3">{test.desc}</p>
                
                {activeTest === test.id && (
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    {language === 'hi' ? 'सक्रिय' : 'Active'}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Recorder */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {tests.find(t => t.id === activeTest)?.name}
        </h2>
        <RecorderCard 
          backendUrl={backendUrl} 
          selectedTest={activeTest} 
          user={user}
          language={language}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('yourActivity')}
              </h3>
              <button className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline">
                {t('viewAll')} →
              </button>
            </div>

            {submissions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎯</div>
                <p className="text-gray-600 dark:text-gray-400">{t('noSubmissions')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.map(sub => (
                  <div
                    key={sub._id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">
                        {sub.test === 'vertical_jump' && '🏀'}
                        {sub.test === 'situps' && '💪'}
                        {sub.test === 'shuttle_run' && '🏃'}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {sub.test.replace('_', ' ').toUpperCase()}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(sub.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {sub.score}
                      </div>
                      <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                        sub.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {sub.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tips & Stats */}
        <div className="space-y-6">
          {/* Quick Tips */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-blue-100 dark:border-blue-800">
            <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-4 flex items-center gap-2">
              💡 {t('quickTips')}
            </h3>
            <ul className="space-y-3 text-sm text-blue-800 dark:text-blue-300">
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                <span>{t('tip1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                <span>{t('tip2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                <span>{t('tip3')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                <span>{t('tip4')}</span>
              </li>
            </ul>
          </div>

          {/* Personal Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              📈 {language === 'hi' ? 'आपके आंकड़े' : 'Your Stats'}
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'hi' ? 'कुल टेस्ट' : 'Total Tests'}
                </span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {submissions.length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'hi' ? 'स्वीकृत' : 'Approved'}
                </span>
                <span className="text-xl font-bold text-green-600">
                  {submissions.filter(s => s.status === 'approved').length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'hi' ? 'लंबित' : 'Pending'}
                </span>
                <span className="text-xl font-bold text-yellow-600">
                  {submissions.filter(s => s.status === 'pending').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}